import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export const COMPANY = {
  name: 'Quality Air of Texas LLC',
  address: '6023 Pennworth Ln, Houston, TX 77084',
  phone: '(713) 922-5702',
  email: 'qualityairoftexas@gmail.com',
  web: 'https://qualityairoftexas.com',
  /** White-background logo for PDF (source had black bg) */
  logo: '/images/qat-invoice.png',
  /** Transparent logo for on-screen UI */
  logoUi: '/images/qat-transparent.png',
  /** Venmo business — https://account.venmo.com/u/QualityAirofTexas */
  venmoUsername: 'QualityAirofTexas',
  venmoProfileUrl: 'https://account.venmo.com/u/QualityAirofTexas',
  authorizedBy: 'Pejman Ansari',
  warranty:
    'We have one year workmanship warranty on labor and manufacture warranty on parts.',
  notice:
    'We Will Not Be Responsible For Loss or Damage Caused By: Fire, Theft, Testing, Or Any Other Causes beyond Our Control.',
  defaultTerms:
    'Terms: Net Cash. No Goods Held Over 30 Days\n\nNotice: This invoice is valid only if issued and received directly from QualityAirofTexas@gmail.com.'
};

export const ACCESS_STORAGE_KEY = 'pejmanAccessGranted';
export const DRAFT_KEY = 'pejmanInvoiceDraft';
export const LAST_INVOICE_KEY = 'pejmanLastInvoice';
export const RECENT_CLIENTS_KEY = 'pejmanRecentClients';
export const SUGGESTIONS_KEY = 'pejmanServiceSuggestions';

export const DEFAULT_ACCESS_CODE =
  process.env.REACT_APP_PEJMAN_ACCESS_CODE || 'pejman2026';

export const SERVICE_TEMPLATES = {
  'ac-repair': {
    name: 'AC Unit Repair',
    items: [
      { qty: 1, description: 'AC Unit Diagnosis', price: 85, amount: 85 },
      { qty: 1, description: 'Repair/Replace Faulty Component', price: 250, amount: 250 }
    ],
    labor:
      'Diagnosed AC unit, identified faulty compressor, replaced with new unit, tested system operation, recharged refrigerant to proper levels.'
  },
  maintenance: {
    name: 'Annual Maintenance',
    items: [
      { qty: 1, description: 'Full System Inspection', price: 120, amount: 120 },
      { qty: 1, description: 'Filter Replacement', price: 45, amount: 45 },
      { qty: 1, description: 'Coil Cleaning', price: 85, amount: 85 }
    ],
    labor:
      'Performed complete system inspection, replaced air filters, cleaned condenser coils, checked refrigerant levels, tested thermostat operation.'
  },
  installation: {
    name: 'New Installation',
    items: [
      { qty: 1, description: 'New AC Unit - 3 Ton', price: 2500, amount: 2500 },
      { qty: 1, description: 'Installation Labor', price: 800, amount: 800 },
      { qty: 1, description: 'Materials & Supplies', price: 300, amount: 300 }
    ],
    labor:
      'Removed old unit, installed new 3-ton AC system, connected electrical and refrigerant lines, tested for proper operation and efficiency.'
  },
  'filter-change': {
    name: 'Filter Replacement',
    items: [
      { qty: 1, description: 'Premium Air Filter', price: 35, amount: 35 },
      { qty: 1, description: 'Service Call', price: 50, amount: 50 }
    ],
    labor: 'Replaced air filter with high-efficiency filter, inspected system operation.'
  }
};

export function createEmptyForm(isEstimate = false) {
  const ts = Date.now();
  return {
    invoiceNumber: isEstimate ? `EST-${ts}` : `INV-${ts}`,
    serviceDate: new Date().toISOString().split('T')[0],
    clientName: '',
    clientAddress: '',
    clientPhone: '',
    clientEmail: '',
    clientModel: '',
    poNumber: '',
    isEstimate: Boolean(isEstimate),
    lineItems: [{ qty: 1, description: '', price: 0, amount: 0 }],
    laborPerformed: '',
    subtotal: 0,
    taxRate: 0.0825,
    taxAmount: 0,
    discountMode: 'amount',
    discount: 0,
    discountPercent: 0,
    appliedDiscount: 0,
    totalAmount: 0,
    notes: '',
    terms: COMPANY.defaultTerms
  };
}

export function calculateTotals(formData) {
  let subtotal = 0;
  (formData.lineItems || []).forEach((item) => {
    subtotal += parseFloat(item.amount || 0);
  });

  const taxRate = parseFloat(formData.taxRate || 0);
  const taxAmount = subtotal * taxRate;
  const pct = parseFloat(formData.discountPercent || 0);
  const amt = parseFloat(formData.discount || 0);
  let discountRaw =
    formData.discountMode === 'percent' ? subtotal * (pct / 100) : amt;
  const maxDiscount = Math.max(0, subtotal + taxAmount);
  discountRaw = Math.min(Math.max(0, discountRaw), maxDiscount);
  const appliedDiscount = discountRaw;
  const totalAmount = subtotal + taxAmount - appliedDiscount;

  return { subtotal, taxAmount, appliedDiscount, totalAmount };
}

export function discountLineLabel(formData) {
  const applied = Number(formData.appliedDiscount || 0);
  if (applied <= 0) return '';
  if (formData.discountMode === 'percent' && Number(formData.discountPercent) > 0) {
    return `Discount (${Number(formData.discountPercent).toFixed(2)}%)`;
  }
  return 'Discount ($)';
}

export function buildSavePayload(formData) {
  const { appliedDiscount, ...rest } = formData;
  return {
    ...rest,
    discount: Number(appliedDiscount || 0),
    poNumber: formData.poNumber || ''
  };
}

export function money(n) {
  return `$${Number(n || 0).toFixed(2)}`;
}

/** Brand-aligned invoice palette — navy from logo (#012E53) + red accent */
export const INVOICE_PALETTE = {
  primary: [1, 46, 83], // #012E53 — logo navy
  secondary: [30, 64, 110], // deeper navy for rules
  accent: [5, 150, 105], // #059669 — balance due
  brandRed: [192, 32, 32], // #C02020 — logo wave red (sparing use)
  destructive: [192, 32, 32],
  foreground: [15, 23, 42], // #0F172A
  muted: [71, 85, 105], // #475569 — WCAG AA on white
  border: [226, 232, 240], // #E2E8F0
  stripe: [248, 250, 252], // #F8FAFC
  white: [255, 255, 255],
  estimate: [180, 83, 9] // amber-700
};

export const INVOICE_CSS = {
  primary: '#012E53',
  secondary: '#1E406E',
  accent: '#059669',
  brandRed: '#C02020',
  destructive: '#C02020',
  foreground: '#0F172A',
  muted: '#475569',
  border: '#E2E8F0',
  background: '#F8FAFC',
  estimate: '#B45309'
};

/**
 * Load logo as flattened JPEG data URL (reliable in jsPDF) with natural aspect ratio.
 * Source asset is 492×196 — must never be forced into a square.
 */
async function loadLogoForPdf() {
  const src =
    typeof window !== 'undefined'
      ? `${window.location.origin}${COMPANY.logo}`
      : COMPANY.logo;

  const img = await new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
    setTimeout(() => resolve(image.complete ? image : null), 4000);
  });

  if (!img || !img.width) return null;

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // Flatten transparency onto white so navy/red logo colors stay correct in PDF
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);

  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.95),
    width: canvas.width,
    height: canvas.height,
    ratio: canvas.width / canvas.height
  };
}

export async function generateFormalPDF(formData) {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const C = INVOICE_PALETTE;
  const margin = 14;
  let yPos = 16;

  // --- Letterhead: logo (brand mark) left · contact + doc title right ---
  const logo = await loadLogoForPdf();
  // Natural ratio ~2.51:1 — never square-crop
  const logoW = 58;
  const logoH = logo ? logoW / logo.ratio : 23;

  if (logo) {
    try {
      pdf.addImage(logo.dataUrl, 'JPEG', margin, yPos - 1, logoW, logoH);
    } catch (_) {
      /* fall through to text mark */
    }
  } else {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...C.primary);
    pdf.text('QUALITY AIR', margin, yPos + 6);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.text('of Texas LLC', margin, yPos + 11);
  }

  const headerRightX = pageWidth - margin;
  // Contact block — legal name small; logo already carries brand
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(...C.primary);
  pdf.text(COMPANY.name, headerRightX, yPos + 3, { align: 'right' });

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.setTextColor(...C.muted);
  pdf.text(COMPANY.address, headerRightX, yPos + 7.5, { align: 'right' });
  pdf.text(`${COMPANY.phone}  ·  ${COMPANY.email}`, headerRightX, yPos + 11.5, {
    align: 'right'
  });
  pdf.text(COMPANY.web.replace(/^https?:\/\//, ''), headerRightX, yPos + 15.5, {
    align: 'right'
  });

  yPos = Math.max(yPos + logoH + 2, yPos + 20);

  const docTitle = formData.isEstimate ? 'ESTIMATE' : 'INVOICE';
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.setTextColor(...C.primary);
  pdf.text(docTitle, headerRightX, yPos, { align: 'right' });

  if (formData.isEstimate) {
    pdf.setFontSize(8);
    pdf.setTextColor(...C.estimate);
    pdf.text('Valid for 30 days', headerRightX, yPos + 4.5, { align: 'right' });
    yPos += 3;
  }

  yPos += 5;
  // Dual rule: navy + thin brand red accent (formal US letterhead)
  pdf.setDrawColor(...C.primary);
  pdf.setLineWidth(0.8);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  pdf.setDrawColor(...C.brandRed);
  pdf.setLineWidth(0.35);
  pdf.line(margin, yPos + 1.3, pageWidth - margin, yPos + 1.3);
  yPos += 8;

  const leftYStart = yPos;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(9);
  pdf.setTextColor(...C.primary);
  pdf.text('BILL TO', margin, yPos);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...C.foreground);
  yPos += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.text(formData.clientName || '—', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  yPos += 4.5;
  const addrLines = pdf.splitTextToSize(formData.clientAddress || '—', 88);
  pdf.setTextColor(...C.muted);
  pdf.text(addrLines, margin, yPos);
  yPos += addrLines.length * 4.2;
  pdf.text(`Phone: ${formData.clientPhone || '—'}`, margin, yPos);
  yPos += 4.2;
  if (formData.clientEmail) {
    pdf.text(`Email: ${formData.clientEmail}`, margin, yPos);
    yPos += 4.2;
  }
  if (formData.clientModel) {
    pdf.text(`Equipment: ${formData.clientModel}`, margin, yPos);
    yPos += 4.2;
  }

  const rightX = pageWidth / 2 + 6;
  let metaY = leftYStart;
  const meta = [
    [formData.isEstimate ? 'Estimate #' : 'Invoice #', formData.invoiceNumber],
    ['Date', new Date(formData.serviceDate).toLocaleDateString('en-US')],
    ['PO Number', formData.poNumber || '—']
  ];
  meta.forEach(([label, value]) => {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(...C.muted);
    pdf.text(label.toUpperCase(), rightX, metaY);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...C.foreground);
    pdf.text(String(value), pageWidth - margin, metaY, { align: 'right' });
    metaY += 6.5;
  });

  yPos = Math.max(yPos, metaY) + 6;

  const tableRows = (formData.lineItems || []).map((item) => [
    String(item.qty ?? ''),
    item.description || '',
    money(item.price),
    money(item.amount)
  ]);

  autoTable(pdf, {
    startY: yPos,
    head: [['QTY', 'DESCRIPTION', 'RATE', 'AMOUNT']],
    body: tableRows,
    theme: 'striped',
    headStyles: {
      fillColor: C.primary,
      textColor: C.white,
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 3.5
    },
    alternateRowStyles: { fillColor: C.stripe },
    columnStyles: {
      0: { cellWidth: 16, halign: 'center' },
      1: { cellWidth: 94 },
      2: { cellWidth: 35, halign: 'right' },
      3: { cellWidth: 35, halign: 'right' }
    },
    styles: {
      fontSize: 9,
      cellPadding: 3.5,
      textColor: C.foreground,
      lineColor: C.border,
      lineWidth: 0.15
    },
    margin: { left: margin, right: margin }
  });

  yPos = pdf.lastAutoTable ? pdf.lastAutoTable.finalY + 8 : yPos + 40;

  if (formData.laborPerformed) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(...C.primary);
    pdf.text('LABOR / WORK PERFORMED', margin, yPos);
    yPos += 4.5;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...C.foreground);
    const laborLines = pdf.splitTextToSize(formData.laborPerformed, pageWidth - margin * 2);
    pdf.text(laborLines, margin, yPos);
    yPos += laborLines.length * 4 + 4;
  }

  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(8);
  pdf.setTextColor(...C.muted);
  const warrantyLines = pdf.splitTextToSize(COMPANY.warranty, pageWidth - margin * 2);
  pdf.text(warrantyLines, margin, yPos);
  yPos += warrantyLines.length * 3.5 + 6;

  const totalsX = pageWidth - 74;
  const totalsValueX = pageWidth - margin;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...C.muted);
  pdf.text('Subtotal', totalsX, yPos);
  pdf.setTextColor(...C.foreground);
  pdf.text(money(formData.subtotal), totalsValueX, yPos, { align: 'right' });
  yPos += 5.5;

  pdf.setTextColor(...C.muted);
  pdf.text(`Tax (${(Number(formData.taxRate) * 100).toFixed(2)}%)`, totalsX, yPos);
  pdf.setTextColor(...C.foreground);
  pdf.text(money(formData.taxAmount), totalsValueX, yPos, { align: 'right' });
  yPos += 5.5;

  const applied = Number(formData.appliedDiscount || 0);
  if (applied > 0) {
    pdf.setTextColor(...C.destructive);
    pdf.text(discountLineLabel(formData), totalsX, yPos);
    pdf.text(`-$${applied.toFixed(2)}`, totalsValueX, yPos, { align: 'right' });
    yPos += 5.5;
  }

  pdf.setDrawColor(...C.primary);
  pdf.setLineWidth(0.55);
  pdf.line(totalsX - 4, yPos, pageWidth - margin, yPos);
  yPos += 7;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...C.primary);
  pdf.text(formData.isEstimate ? 'ESTIMATED TOTAL' : 'BALANCE DUE', totalsX, yPos);
  pdf.setFontSize(14);
  pdf.setTextColor(...C.accent);
  pdf.text(money(formData.totalAmount), totalsValueX, yPos, { align: 'right' });
  yPos += 8;

  // Venmo payment gate (invoices only)
  if (!formData.isEstimate && Number(formData.totalAmount) > 0) {
    const payUrl = buildVenmoPaymentUrl(formData);
    pdf.setFillColor(...C.stripe);
    pdf.roundedRect(margin, yPos, pageWidth - margin * 2, 16, 1.5, 1.5, 'F');
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.setTextColor(...C.primary);
    pdf.text('PAY WITH VENMO', margin + 3, yPos + 5);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...C.foreground);
    pdf.text(`@${COMPANY.venmoUsername}  ·  ${money(formData.totalAmount)}`, margin + 3, yPos + 10);
    // Short note — full URL may be long; profile is reliable on PDF
    pdf.setTextColor(...C.muted);
    pdf.setFontSize(7);
    const urlLines = pdf.splitTextToSize(payUrl, pageWidth - margin * 2 - 6);
    // Keep one line under box if fits; else profile URL
    if (urlLines.length === 1) {
      pdf.text(urlLines[0], margin + 3, yPos + 13.2);
    } else {
      pdf.text(COMPANY.venmoProfileUrl, margin + 3, yPos + 13.2);
    }
    yPos += 20;
  } else {
    yPos += 2;
  }

  if (formData.notes) {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(...C.primary);
    pdf.text('NOTES', margin, yPos);
    yPos += 4;
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(...C.foreground);
    const noteLines = pdf.splitTextToSize(formData.notes, pageWidth - margin * 2);
    pdf.text(noteLines, margin, yPos);
    yPos += noteLines.length * 3.5 + 5;
  }

  if (yPos > pageHeight - 55) {
    pdf.addPage();
    yPos = 20;
  }

  pdf.setDrawColor(...C.border);
  pdf.setLineWidth(0.3);
  pdf.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(8);
  pdf.setTextColor(...C.foreground);
  pdf.text('IMPORTANT NOTICE', margin, yPos);
  yPos += 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...C.muted);
  const noticeLines = pdf.splitTextToSize(COMPANY.notice, pageWidth - margin * 2);
  pdf.text(noticeLines, margin, yPos);
  yPos += noticeLines.length * 3.5 + 4;

  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...C.foreground);
  pdf.text('TERMS & CONDITIONS', margin, yPos);
  yPos += 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(...C.muted);
  const termsLines = pdf.splitTextToSize(formData.terms || COMPANY.defaultTerms, pageWidth - margin * 2);
  pdf.text(termsLines, margin, yPos);
  yPos += termsLines.length * 3.5 + 8;

  pdf.setFont('helvetica', 'italic');
  pdf.setFontSize(9);
  pdf.setTextColor(...C.primary);
  pdf.text(`Authorized By: ${COMPANY.authorizedBy}`, pageWidth - margin, yPos, {
    align: 'right'
  });

  // Outer frame — drawn last so it never clips the logo
  pdf.setDrawColor(...C.primary);
  pdf.setLineWidth(0.9);
  pdf.rect(8, 8, pageWidth - 16, pageHeight - 16);

  return pdf;
}

/** Note prefilled in Venmo for this document */
export function buildVenmoNote(formData) {
  const num = formData.invoiceNumber || '';
  if (formData.isEstimate) return `Estimate ${num} — Quality Air of Texas`;
  return `Invoice ${num} — Quality Air of Texas`;
}

/**
 * HTTPS Venmo pay link (shareable to customers).
 * Prefills amount = balance due and invoice note.
 * Opens Venmo app on mobile when installed.
 */
export function buildVenmoPaymentUrl(formData) {
  const amount = Number(formData.totalAmount || 0);
  const params = new URLSearchParams({
    txn: 'pay',
    amount: amount > 0 ? amount.toFixed(2) : '0.00',
    note: buildVenmoNote(formData)
  });
  return `https://venmo.com/${COMPANY.venmoUsername}?${params.toString()}`;
}

/** Native app scheme — best when opening on-device with Venmo installed */
export function buildVenmoAppUrl(formData) {
  const amount = Number(formData.totalAmount || 0);
  const note = encodeURIComponent(buildVenmoNote(formData));
  const amt = amount > 0 ? amount.toFixed(2) : '0.00';
  return `venmo://paycharge?txn=pay&recipients=${COMPANY.venmoUsername}&amount=${amt}&note=${note}`;
}

/** Short SMS / share blurb with payment link (invoices) */
export function buildVenmoPaymentMessage(formData) {
  const url = buildVenmoPaymentUrl(formData);
  const total = money(formData.totalAmount);
  if (formData.isEstimate) {
    return (
      `${COMPANY.name}\n` +
      `Estimate #${formData.invoiceNumber}\n` +
      `Total: ${total}\n` +
      `Venmo: @${COMPANY.venmoUsername}\n${COMPANY.venmoProfileUrl}`
    );
  }
  return (
    `${COMPANY.name}\n` +
    `Invoice #${formData.invoiceNumber}\n` +
    `Amount due: ${total}\n\n` +
    `Pay with Venmo:\n${url}\n\n` +
    `Or find us @${COMPANY.venmoUsername}`
  );
}

export function buildTextSummary(formData) {
  const docType = formData.isEstimate ? 'ESTIMATE' : 'INVOICE';
  const discLabel = discountLineLabel(formData);
  const venmoBlock = formData.isEstimate
    ? ''
    : `\nPay with Venmo (@${COMPANY.venmoUsername}):\n${buildVenmoPaymentUrl(formData)}\n`;
  return (
    `${docType} #${formData.invoiceNumber}\n` +
    `${COMPANY.name}\n` +
    `Date: ${formData.serviceDate}\n` +
    (formData.poNumber ? `PO #: ${formData.poNumber}\n` : '') +
    `Client: ${formData.clientName}\n` +
    `Phone: ${formData.clientPhone || 'N/A'}\n` +
    `Address: ${formData.clientAddress || 'N/A'}\n` +
    `Items:\n` +
    (formData.lineItems || [])
      .map(
        (item) =>
          `  • ${item.qty}x ${item.description} - ${money(item.amount)}`
      )
      .join('\n') +
    `\nSubtotal: ${money(formData.subtotal)}\n` +
    `Tax: ${money(formData.taxAmount)}\n` +
    (Number(formData.appliedDiscount || 0) > 0
      ? `${discLabel}: -${money(formData.appliedDiscount)}\n`
      : '') +
    `TOTAL: ${money(formData.totalAmount)}\n` +
    venmoBlock +
    `${COMPANY.phone} | ${COMPANY.email}`
  );
}
